import React, { useMemo, useState } from "react";
import { Modal } from "../../../../components/modal/Modal";
import { useTranslation } from "react-i18next";
import { useAtiraFormContext } from "../../context/form-context";
import { Flex } from "../../../../components/Flex";
import { Controller, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { AtiraSelect } from "../../../../components/AtiraSelect";
import { Button } from "../../../../components/Button";
import { AtiraToast } from "../../../../utils/AtiraToast";
import { formActions } from "../../../../redux/forms/form.slice";
import { onSetTypedCRUDErrors } from "../../../../utils/onSetTypedCRUDErrors";
import { Info } from "../../../../components/Info";
import { formSliceSelectors } from "../../../../redux/forms/form.selector";
import { AddRemoveFormFromToFolderDto } from "../../../../model/form/dto/AddRemoveFormFromToFolderDto";
import { TestIDs } from "../../../../tests/types/testIds";

export const FormAddToFolderModal: React.FC = () => {
  const [loading, setLoading] = useState(false);

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { selectedForm, formAddToFolderVisible, setFormAddToFolderVisible } =
    useAtiraFormContext();
  const {
    control,
    reset,
    getValues,
    handleSubmit,
    setError,
    formState: { isValid },
  } = useForm<AddRemoveFormFromToFolderDto>({
    defaultValues: {
      mode: "add",
      userId,
    },
  });

  const folders = useAppSelector(formSliceSelectors.selectFolders);

  const folderOptions = useMemo(
    () =>
      folders?.map((folder) => ({
        label: folder.name,
        value: folder._id,
        "data-testid": TestIDs.forms.form.addToFolder.modal.dropdown.option(
          folder._id,
        ),
      })),
    [folders],
  );

  const localOnClose = () => {
    setFormAddToFolderVisible(false);
    reset();
  };

  const onMove = async () => {
    try {
      if (!selectedForm?._id) {
        return;
      }

      setLoading(true);

      const dto = getValues();

      await dispatch(
        formActions.addRemoveFormToFolder({ ...dto, formId: selectedForm._id }),
      ).unwrap();
      await dispatch(formActions.getFolders({ userId })).unwrap();

      AtiraToast.success(
        t("forms.addToFolder.success", {
          name: folderOptions.find((f) => f.value === dto.formFolderId)?.label,
        }),
      );

      localOnClose();
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t("forms.addToFolder.title", {
        name: selectedForm?.title,
      })}
      open={formAddToFolderVisible}
      onClose={localOnClose}
    >
      <Flex width={"100%"} flexDirection="column" gap="m">
        <Info info={t("forms.addToFolder.info")} />

        <Controller
          control={control}
          name="formFolderId"
          rules={{
            required: {
              value: true,
              message: t("common.fieldRequired"),
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <AtiraSelect
              title={t("common.folder")}
              options={folderOptions}
              value={folderOptions?.find((f) => f.value === value)}
              onChange={onChange}
              valid={!error}
              errorMessage={error?.message}
              info={t("forms.addToFolder.folder.info")}
              required
              className={TestIDs.forms.form.addToFolder.modal.dropdown.open(
                selectedForm?._id || "",
              )}
            />
          )}
        />

        <Button
          onClick={handleSubmit(onMove)}
          disabled={!isValid}
          loading={loading}
          margin="none"
          dataTestId={TestIDs.forms.form.addToFolder.modal.button}
        >
          {t("common.move")}
        </Button>
      </Flex>
    </Modal>
  );
};
